import React, { useState } from 'react';
import API_BASE_URL from '../../services/api';

const ReturnPolicy = () => {
  const [formData, setState] = useState({
    name: '',
    email: '',
    orderNumber: '',
    reason: '',
    productName: '',
    description: ''
  });
  const [submitStatus, setSubmitStatus] = useState({
    submitted: false,
    success: false,
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setState(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitStatus({ submitted: true, success: true, message: 'Processing your request...' });
    
    // Send the return request to the admin dashboard API
    fetch(`${API_BASE_URL}/admin/return-requests`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        orderId: formData.orderNumber,
        email: formData.email,
        reason: formData.reason,
        details: formData.description,
        timestamp: new Date().toISOString(),
        status: 'pending'
      })
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json();
    })
    .then(data => {
      setSubmitStatus({ 
        submitted: true, 
        success: true, 
        message: 'Return request submitted successfully. We will contact you via email with further instructions.' 
      });
      
      // Reset form after successful submission
      setState({
        name: '',
        email: '',
        orderNumber: '',
        reason: '',
        productName: '',
        description: ''
      });
    })
    .catch(error => {
      console.error('Error submitting return request:', error);
      setSubmitStatus({ 
        submitted: true, 
        success: false, 
        message: 'There was an error submitting your request. Please try again or contact support.' 
      });
    });
  };

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 font-handwritten">Return Policy</h1>
      <div className="space-y-6 text-gray-700 dark:text-gray-300">
        <section>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Return Eligibility</h3>
          <p className="mb-2">At Ry&#39;s Circus, we want you to be completely satisfied with your purchase. If you&#39;re not entirely happy with your order, we&#39;re here to help.</p>
          
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mt-3">
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Return Eligibility</h4>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>The Ryldo:</strong> Not eligible for return for health and safety reasons</li>
              <li><strong>Other Products:</strong> Eligible for return only in cases of damage or if the wrong product was sent</li>
              <li>Products must be unused, unworn, and in original condition</li>
              <li>Original packaging must be intact and included</li>
              <li>Proof of purchase (order number or receipt) is required</li>
              <li>Items marked as &quot;Final Sale&quot; or &quot;Non-Returnable&quot; cannot be returned</li>
            </ul>
          </div>
        </section>
        
        <section>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Return Process</h3>
          <p className="mb-2">To initiate a return, please follow these steps:</p>
          
          <ol className="list-decimal pl-5 space-y-2 mt-2">
            <li>Fill out the return request form below</li>
            <li>Our team will review your request and contact you via email</li>
            <li>If your return is approved, you will receive a Return Authorization Number (RAN) and shipping instructions</li>
            <li>Package the item(s) securely in the original packaging if possible</li>
            <li>Include the RAN on the outside of the package</li>
            <li>Ship the package to the address provided in the return instructions</li>
          </ol>
        </section>
        
        <section>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Return Shipping</h3>
          <p className="mb-2">Customers are responsible for return shipping costs unless the return is due to our error (wrong item shipped, damaged or defective product). We recommend using a trackable shipping method as we can&#39;t process returns that are lost in transit.</p>
          <p>For returns due to our error (wrong item shipped, defective product, etc.), we will provide a prepaid return shipping label.</p>
        </section>
        
        <section>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Return Timeframe</h3>
          <p className="mb-2">Returns must be initiated within 30 days of receiving your order. Items received after the 30-day return window may not be accepted for return or may be subject to a restocking fee.</p>
          
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mt-3">
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Processing Time</h4>
            <p>Once we receive your return, please allow 5-7 business days for inspection and processing. Refunds will be issued to the original payment method within 3-5 business days after processing.</p>
          </div>
        </section>
        
        <section>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Damaged or Defective Items</h3>
          <p className="mb-2">If you receive a damaged or defective item, please use the return request form below within 48 hours of receipt. We&#39;ll review your request and arrange for a return and replacement if eligible.</p>
          <p>Damaged or defective items will be replaced or refunded at our discretion. Return shipping for these items will be covered by Ry&#39;s Circus.</p>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Return Request Form</h3>
          {submitStatus.submitted && (
            <div className={`p-4 mb-4 rounded-md ${submitStatus.success ? 'bg-green-50 dark:bg-green-900' : 'bg-red-50 dark:bg-red-900'}`}>
              <p className={`text-${submitStatus.success ? 'green' : 'red'}-700 dark:text-${submitStatus.success ? 'green' : 'red'}-200`}>
                {submitStatus.message}
              </p>
            </div>
          )}
          <form onSubmit={handleSubmit} className="mt-4 space-y-4 bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Full Name</label>
                <input 
                  type="text" 
                  id="name" 
                  name="name" 
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white" 
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email Address</label>
                <input 
                  type="email" 
                  id="email" 
                  name="email" 
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white" 
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="orderNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Order Number</label>
              <input 
                type="text" 
                id="orderNumber" 
                name="orderNumber" 
                value={formData.orderNumber}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white" 
              />
            </div>
            
            <div>
              <label htmlFor="productName" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Product Name</label>
              <input 
                type="text" 
                id="productName" 
                name="productName" 
                value={formData.productName}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white" 
              />
            </div>
            
            <div>
              <label htmlFor="reason" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Reason for Return</label>
              <select 
                id="reason" 
                name="reason" 
                value={formData.reason}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
              >
                <option value="">Select a reason</option>
                <option value="damaged">Product arrived damaged</option>
                <option value="wrong-item">Received wrong product</option>
                <option value="defective">Product is defective</option>
                <option value="other">Other (please specify)</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Additional Details</label>
              <textarea 
                id="description" 
                name="description" 
                rows="4" 
                value={formData.description}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                placeholder="Please provide details about your return request..."
              ></textarea>
            </div>
            
            <div className="pt-2">
              <button 
                type="submit" 
                className="w-full md:w-auto px-6 py-2 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={submitStatus.submitted && !submitStatus.success}
              >
                {(submitStatus.submitted && !submitStatus.success) ? 'Processing...' : 'Submit Return Request'}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
};

export default ReturnPolicy;
